import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, Row, Select, Typography, message, Spin } from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setBreadcrumbItems } from "../../store/actions";
import { debounce } from "lodash";

const { Text } = Typography;
const { Option } = Select;

const AddAddress = ({ setBreadcrumbItems }) => {
  const [addressDetails, setAddressDetails] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);

  // Search Options
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [fetchingCity, setFetchingCity] = useState(false);

  const navigate = useNavigate();
  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;

  const breadcrumbItems = [
    { title: "Address", link: "#" },
    { title: "Add Address", link: "#" },
  ];

  // Fetch States on Mount
  useEffect(() => {
    setBreadcrumbItems("Add Address", breadcrumbItems);
    // Hardcoded list of Indian States
    const states = [
      "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
      "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli", "Daman and Diu", "Delhi", "Goa",
      "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka",
      "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
      "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
      "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
    ];
    setStateOptions(states.map(s => ({ label: s, value: s })));
  }, [setBreadcrumbItems]);

  // City Search (Using Photon API for Autocomplete - Faster & More Permissive than Nominatim)
  const fetchCityList = async (value) => {
    if (!value) return;
    setFetchingCity(true);
    setCityOptions([]);
    try {
      // Photon Search query: "Name, State"
      const query = `${value}, ${state}`;

      const res = await axios.get(`https://photon.komoot.io/api/`, {
        params: {
          q: query,
          limit: 15,
          lang: 'en'
        }
      });

      const features = res.data.features || [];
      const options = features.map(item => {
        const props = item.properties;
        const coords = item.geometry.coordinates; // [lon, lat]

        // Construct readable label
        const name = props.name;
        const placeState = props.state || '';
        const district = props.district || props.city || '';

        // Filter out irrelevant results if possible, or just show all
        const label = `${name}${district ? `, ${district}` : ''}${placeState ? `, ${placeState}` : ''}`;

        return {
          label: label,
          value: name, // The name to set in input
          parsed: {
            pincode: props.postcode,
            lat: coords[1], // Latitude
            lon: coords[0], // Longitude
            displayName: label,
            state: props.state
          }
        };
      });

      // Remove duplicates
      const uniqueOptions = [...new Map(options.map(item => [item.label, item])).values()];
      setCityOptions(uniqueOptions);

    } catch (e) {
      console.error(e);
      // Fallback message, don't show generic error to user immediately to avoid spam
    } finally {
      setFetchingCity(false);
    }
  };

  const debouncedFetchCity = useMemo(() => debounce(fetchCityList, 400), [state]);

  const handleStateChange = (value) => {
    setState(value);
    setCity("");
    setCityOptions([]);
    setLat(null);
    setLon(null);
    setPincode("");
  };

  const handleCitySelect = (value, option) => {
    setCity(value);
    const data = option.parsed;
    if (data.pincode) setPincode(data.pincode);
    if (data.lat) setLat(data.lat);
    if (data.lon) setLon(data.lon);

    // Auto-fill of Address Details REMOVED as per user request
  };

  const handleSubmit = async () => {
    if (!addressDetails.trim() || !state || !city) {
      message.warning("Please enter all required fields");
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASEURL}/address`,
        {
          addressDetails,
          city,
          state,
          pincode,
          latitude: lat,
          longitude: lon
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            accept: "*/*",
          },
        }
      );
      message.success("Address added successfully");
      navigate("/address-listing");
    } catch (error) {
      console.error(error);
      message.error("Failed to add address");
    }
  };

  return (
    <div className="p-4">
      <Card style={{ marginTop: 20 }} title="Add Address">

        <Row>
          <Col span={24}>
            <Text strong>Address</Text>
            <Input
              placeholder="Enter Full Address"
              value={addressDetails}
              onChange={(e) => setAddressDetails(e.target.value)}
              style={{ marginBottom: 20 }}
            />
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Text strong>State</Text>
            <Select
              placeholder="Select State"
              value={state || undefined}
              onChange={handleStateChange}
              style={{ width: "100%", marginBottom: 20 }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {stateOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Text strong>City / Village</Text>
            <Select
              showSearch
              placeholder={state ? "Start typing to find Village/City..." : "Select State first"}
              value={city || undefined}
              onSearch={debouncedFetchCity}
              onChange={handleCitySelect}
              filterOption={false}
              notFoundContent={fetchingCity ? <Spin size="small" /> : null}
              style={{ width: "100%", marginBottom: 20 }}
              disabled={!state}
            >
              {cityOptions.map((opt, idx) => (
                <Option key={`${opt.value}-${idx}`} value={opt.value} parsed={opt.parsed}>{opt.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Text strong>Pincode</Text>
            <Input
              placeholder="Enter Pincode"
              value={pincode}
              onChange={(e) => setPincode(e.target.value)}
              style={{ marginBottom: 20 }}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col>
            <Button
              type="primary"
              style={{ backgroundColor: PRIMARY_RED }}
              icon={<PlusOutlined />}
              onClick={handleSubmit}
            >
              Add Address
            </Button>
          </Col>
          <Col>
            <Button
              type="link"
              style={{ color: PRIMARY_RED }}
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/address-listing")}
            >
              Back
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(AddAddress);